import type { AccountStateReason } from '@/types/generated/schemas';

const accountStateReasons = {
  ACCOUNT_INACTIVE: true,
  ORGANIZATION_CHANGED: true,
  TOKEN_REVOKED: true,
  REAUTHENTICATE: true,
  ACCOUNT_CONFLICT: true,
  ACCOUNT_STATE_UNAVAILABLE: true,
  ACCOUNT_OPERATION_IN_PROGRESS: true,
  MAINTENANCE_FREEZE: true,
  STALE_VERSION: true,
  ACTION_NOT_ALLOWED: true,
} satisfies Record<AccountStateReason, true>;

export const getAccountStateReason = (error: unknown): AccountStateReason | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const value = error as { reason?: unknown; response?: { data?: { reason?: unknown } } };
  const reason = value.response?.data?.reason ?? value.reason;
  return typeof reason === 'string' && Object.hasOwn(accountStateReasons, reason)
    ? (reason as AccountStateReason)
    : undefined;
};

export const isTerminalAccountReason = (
  reason: AccountStateReason | undefined,
): reason is Extract<
  AccountStateReason,
  'ACCOUNT_INACTIVE' | 'ORGANIZATION_CHANGED' | 'TOKEN_REVOKED' | 'REAUTHENTICATE' | 'ACCOUNT_CONFLICT'
> =>
  reason === 'ACCOUNT_INACTIVE' ||
  reason === 'ORGANIZATION_CHANGED' ||
  reason === 'TOKEN_REVOKED' ||
  reason === 'REAUTHENTICATE' ||
  reason === 'ACCOUNT_CONFLICT';

export const getSharedAccountStateMessage = (reason: AccountStateReason): string | undefined => {
  switch (reason) {
    case 'ACCOUNT_OPERATION_IN_PROGRESS':
      return 'An account operation is in progress. Try again shortly.';
    case 'MAINTENANCE_FREEZE':
      return 'Account changes are temporarily paused for planned maintenance.';
    case 'ACCOUNT_STATE_UNAVAILABLE':
      return 'Account state is temporarily unavailable. Please try again.';
    default:
      return undefined;
  }
};

export const getMaintenanceEndLabel = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const response = error as {
    response?: { data?: Record<string, unknown> };
    data?: Record<string, unknown>;
    maintenanceWindowEnd?: unknown;
    maintenanceEndAt?: unknown;
    plannedEndAt?: unknown;
  };
  const body = response.response?.data ?? response;
  const data = body.data && typeof body.data === 'object' ? (body.data as Record<string, unknown>) : undefined;
  const details =
    data?.details && typeof data.details === 'object' ? (data.details as Record<string, unknown>) : undefined;
  const end =
    body.maintenanceWindowEnd ??
    body.maintenanceEndAt ??
    body.plannedEndAt ??
    data?.maintenanceWindowEnd ??
    data?.maintenanceEndAt ??
    data?.plannedEndAt ??
    details?.maintenanceWindowEnd ??
    details?.maintenanceEndAt ??
    details?.plannedEndAt;
  if (typeof end !== 'string' || !end) return undefined;
  const parsed = new Date(end);
  return Number.isNaN(parsed.getTime()) ? end : parsed.toLocaleString();
};
